import React, { useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { Button, Col, Placeholder, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../../../components/Popup/Popup";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { getDifficultyColor } from "../../Module.function";
import { ModuleExercisesProps } from "./ModuleExercises.d";

const ModuleExercises = ({ language, exercises, exercisesLoaded, onEditSave }: ModuleExercisesProps) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExercises, setSelectedExercises] = useState<any[]>([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(selectedExercises);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);
    setSelectedExercises(reordered);
  };

  const handleSave = () => {
    setIsLoadingModal(true);
    onEditSave(
      selectedExercises.map((exercise) => exercise.id),
      () => {
        setIsLoadingModal(false);
        setShowEditModal(false);
      }
    );
  };

  return (
    <React.Fragment>
      <div className="col-lg">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h3 className="mb-0 m-bold">{translate(language, "PAGES.MODULE.EXERCISES.DETAIL")}</h3>
          <div className="d-flex">
            <DstButton
              btnClass="me-2"
              variant="secondary"
              btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
              value={translate(language, "PAGES.MODULE.EXERCISES.EDIT")}
              clickFunction={() => {
                setSelectedExercises(exercises);
                setShowEditModal(true);
              }}
            />
          </div>
        </div>
        <div id="module-exercises-list">
          {!exercisesLoaded ? (
            <div className="mt-3">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="border rounded-3 p-3 shadow-sm mb-3">
                  <Row className="g-3 align-items-center">
                    <Col xs={12} md={8}>
                      <Placeholder as="h3" animation="glow" className="mb-2">
                        <Placeholder xs={4} />
                      </Placeholder>
                      <Placeholder as="div" animation="glow">
                        <Placeholder xs={7} /> <Placeholder xs={8} /> <Placeholder xs={4} />
                      </Placeholder>
                    </Col>
                    <Col xs={12} md={4} className="pt-2 pt-md-0 border-mobile-top border-md-start ps-md-3">
                      <Placeholder as="p" animation="glow" className="mb-2 text-md-end">
                        <Placeholder xs={6} /> <Placeholder xs={4} />
                      </Placeholder>
                      <Placeholder as="p" animation="glow" className="mb-2 text-md-end">
                        <Placeholder xs={5} /> <Placeholder xs={3} />
                      </Placeholder>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          ) : exercises.length === 0 ? (
            <div className="text-center text-muted mt-4">
              <span className="dst-icon-info-circle me-2" />
              {translate(language, "PAGES.MODULE.EXERCISES.NONE_FOUND")}
            </div>
          ) : (
            exercises.map((exercise) => (
              <Row key={exercise.id}>
                <Col xs={12} className="mb-3">
                  <div className="border rounded-3 p-3 shadow-sm">
                    <Row className="d-flex align-items-center g-3">
                      <Col xs={12} md={8}>
                        <div className="d-flex align-items-center mb-1">
                          <h3 className="m-bold mb-0" title={exercise.name}>
                            {exercise.is_deprecated && (
                              <span
                                className="dst-icon-alert-triangle text-danger me-2"
                                title={translate(language, "PAGES.MODULE.EXERCISES.DEPRECATED")}
                              />
                            )}
                            <Link to={`/exercise/${exercise.id}`} className="text-decoration-none">
                              {exercise.name}
                            </Link>{" "}
                          </h3>
                          <span
                            className={`badge ${getDifficultyColor(exercise.difficulty)} ms-2`}
                            title={`${translate(language, "PAGES.MODULE.EXERCISES.DIFFICULTY").replace(
                              "{{LEVEL}}",
                              `${exercise.difficulty}/10`
                            )}`}
                          >
                            {exercise.difficulty}
                          </span>
                        </div>
                        <p className="s-regular text-muted mb-2">{exercise.description}</p>
                        <div className="d-flex flex-wrap gap-2 m-regular">
                          <span className="badge bg-light text-dark border">
                            <span className="dst-icon-clock me-1" />
                            {exercise.duration} min
                          </span>
                          <span className="badge bg-light text-dark border">
                            <span className="dst-icon-file-02 me-1" />
                            {exercise.type.toUpperCase()}
                          </span>
                          <span className="badge bg-light text-dark border text-truncate" title={exercise.filename}>
                            <span className="dst-icon-file-code-01 me-1" />
                            {exercise.filename}
                          </span>
                        </div>
                      </Col>

                      <Col
                        xs={12}
                        md={4}
                        className="text-md-end border-mobile-top border-md-start ps-md-3 pt-2 pt-md-0 s-regular"
                      >
                        <div>
                          <span className="dst-icon-user-01 me-1" />
                          {exercise.owner ? (
                            <Link to={`/user/${exercise.owner.id}`}>
                              {exercise.owner.first_name} {exercise.owner.last_name}
                            </Link>
                          ) : (
                            translate(language, "PAGES.MODULE.EXERCISES.UNKNOWN_AUTHOR")
                          )}
                        </div>
                        <div>
                          <span className="dst-icon-calendar-plus-01 me-1" />
                          {translate(language, "PAGES.MODULE.EXERCISES.CREATED")}{" "}
                          {formatDateHumanly(language, exercise.dt_created)}
                        </div>
                        {exercise?.dt_updated && (
                          <div>
                            <span className="dst-icon-clock-refresh me-1" />
                            {translate(language, "PAGES.MODULE.EXERCISES.UPDATED")}{" "}
                            {formatDateHumanly(language, exercise.dt_updated)}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            ))
          )}
        </div>
      </div>
      <Popup
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        modalTitle={translate(language, "PAGES.MODULE.EXERCISES.EDIT_MODAL.TITLE")}
        size="xl"
        footerContent={
          <React.Fragment>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              {translate(language, "GLOBAL.CANCEL")}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={isLoadingModal}>
              {isLoadingModal ? <Spinner size="sm" /> : translate(language, "GLOBAL.SAVE")}
            </Button>
          </React.Fragment>
        }
      >
        <div className="mb-4">
          {!exercisesLoaded ? (
            <Spinner />
          ) : selectedExercises.length === 0 ? (
            <div className="text-muted">{translate(language, "PAGES.MODULE.EXERCISES.EDIT_MODAL.NO_MODULE")}</div>
          ) : (
            <div className="border">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="exercises-list">
                  {(provided) => (
                    <ul className="list-group list-group-flush" ref={provided.innerRef} {...provided.droppableProps}>
                      {selectedExercises.map((exercise, index) => (
                        <Draggable key={exercise.id.toString()} draggableId={exercise.id.toString()} index={index}>
                          {(provided, snapshot) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`list-group-item d-flex justify-content-between align-items-center ${
                                snapshot.isDragging ? "off-white-background shadow-sm" : ""
                              }`}
                            >
                              <div className="d-flex align-items-center">
                                <span className="dst-icon-menu-01 me-3 text-muted" />
                                <span>
                                  <b className="me-2">{exercise.name}</b>
                                  <span className="badge bg-light text-dark border me-2" title={exercise.filename}>
                                    <span className="dst-icon-file-code-01 me-1" />
                                    {exercise.filename}
                                  </span>
                                </span>
                              </div>
                              <DstButton
                                variant="outline-danger"
                                btnImageBefore={<span className="dst-icon-trash-01" />}
                                value=""
                                clickFunction={() =>
                                  setSelectedExercises((prev) => prev.filter((e) => e.id !== exercise.id))
                                }
                              />
                            </li>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
        </div>
        <MultiSelectLiveDropdown
          category="exercises"
          label={
            <React.Fragment>
              <span className="dst-icon-package-plus me-3 align-content-center blue-font" />
              <span className="blue-font">
                {translate(language, "PAGES.MODULE.EXERCISES.EDIT_MODAL.BUTTONS.ADD_EXERCISES")}
              </span>
            </React.Fragment>
          }
          toggleClass="btn-secondary mb-1"
          language={language}
          clearOnConfirm
          onConfirm={(items) => {
            setSelectedExercises((prev) => {
              const newExercises = items.map((item) => item.value);
              return [...prev.filter((exercise) => !newExercises.some((n) => n.id === exercise.id)), ...newExercises];
            });
          }}
        />
      </Popup>
    </React.Fragment>
  );
};

export default ModuleExercises;
